/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */

import React, { useState } from 'react'
import { css } from '@emotion/react'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { AnimatePresence, motion } from 'motion/react'

import { ChevronDownIcon } from '@slite/icons'

import { OrbitWithImage } from '../../QuoteWithOrbit'
import { NavMenuCard } from './NavMenuCard'
import { navItemBaseCSS } from './styles'

// Compact quote shown in the right side of a dropdown (Solutions today —
// swappable per-item so each category can showcase a different customer).
type NavMenuQuote = {
  name: string
  title: string
  testimonial: string
  pictureUrl: string
  companyImageUrl?: string
}

export type NavMenuChildItem = {
  icon?: React.ReactNode
  title: React.ReactNode
  subtitle?: string
  url: string
  // Optional — when set, hovering this card swaps the quote panel on the
  // right of the dropdown to this customer.
  quote?: NavMenuQuote
}

// Marks a column as a feature column: its items render without icons and the
// image shows underneath (e.g. the ebook cover in Resources).
type NavMenuFeatureImage = {
  src: string
  alt: string
}

type NavMenuColumn = {
  title?: string
  items: NavMenuChildItem[]
  feature?: NavMenuFeatureImage
  continuesPreviousColumn?: boolean
}

export type NavMenuItem = {
  id?: string
  title: React.ReactNode
  url?: string
  columns?: NavMenuColumn[]
  secondaryColumns?: NavMenuColumn[]
}

type Props = {
  items: NavMenuItem[]
}

const HOVER_INDICATOR_LAYOUT_ID = 'navbar-hover-indicator'

const rootCSS = css`
  position: relative;
`

const listCSS = css`
  display: flex;
  align-items: center;
  gap: 0px;
  margin: 0;
  padding: 0;
  list-style: none;
`

const itemCSS = css`
  position: relative;
`

const triggerCSS = css`
  ${navItemBaseCSS};
  position: relative;
  border: none;
  background: transparent;
  cursor: pointer;
  outline: none;
`

const hoverIndicatorCSS = css`
  position: absolute;
  inset: 0;
  background: #f9efe4;
  border-radius: 999px;
  z-index: 0;
  /* Inverted bar (over a dark hero): translucent white instead of cream. */
  .navbar-inverted & {
    background: rgba(255, 255, 255, 0.16);
  }
`

const triggerLabelCSS = css`
  position: relative;
  z-index: 1;
`

const chevronCSS = css`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
`

const columnTitleCSS = css`
  font-family: var(--font-family-system);
  font-size: 13px;
  font-weight: 400;
  color:  rgba(0,0,0,0.5);
  padding: 10px 8px 10px;
`

const primaryCardCSS = css`
  display: flex;
  align-items: flex-start;
  padding: 12px;
  padding-bottom: 16px;
  background: var(--shade-100);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 16px;
`

const secondarySectionCSS = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 8px;
`

// Quote panel sits in the blurred glass viewport next to the primary card.
// Transparent background — the viewport's backdrop-blur shows through.
const quotePanelCSS = css`
  width: 280px;
  padding: 20px 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex-shrink: 0;
`

const quoteOrbitWrapperCSS = css`
  display: flex;
  justify-content: flex-start;
  margin-left: 40px;
`

/* Testimonial + name copy mirror the NavMenuCard title style (15/600/20px,
   shade-05) so the panel feels typographically continuous with the menu
   items. The author description mirrors the subtitle style (13/400,
   rgba(0,0,0,0.5)). Any change here should track NavMenuCard. */
const quoteTestimonialCSS = css`
  font-family: var(--font-family-system);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--shade-05);
  margin: 0;
`

const quoteAttributionWrapperCSS = css`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const quoteNameCSS = css`
  font-family: var(--font-family-system);
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  color: var(--shade-05);
`

const quoteTitleCSS = css`
  font-family: var(--font-family-system);
  font-size: 13px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
`

const secondaryLinkCSS = css`
  display: block;
  font-family: var(--font-family-system);
  font-size: 15px;
  font-weight: 600;
  color: var(--shade-05);
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 8px;
  &:hover {
    background: white;
  }
`

const HORIZONTAL_SLIDE_DISTANCE = 64

const contentCSS = css`
  /*
   * The ACTIVE Content stays in normal flow so its natural width feeds Radix's
   * ResizeObserver → Viewport CSS var → Viewport size. The EXITING Content
   * goes absolute so it doesn't push the new Content out of place during the
   * crossfade swap. (Radix doesn't set data-state on Content in viewport mode,
   * only data-motion — so we key off that.)
   */
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
  width: max-content;
  &[data-motion='to-start'],
  &[data-motion='to-end'] {
    position: absolute;
    top: 0;
    left: 0;
  }
  /* Switch animations (also serve as the entry/exit for menu-to-menu). */
  &[data-motion='from-start'] {
    animation: navMenuContentFromStart 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  &[data-motion='from-end'] {
    animation: navMenuContentFromEnd 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  &[data-motion='to-start'] {
    animation: navMenuContentToStart 0.15s cubic-bezier(0.4, 0, 0.2, 1)
      forwards;
  }
  &[data-motion='to-end'] {
    animation: navMenuContentToEnd 0.15s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  @keyframes navMenuContentFromStart {
    from {
      opacity: 0;
      transform: translateX(-${HORIZONTAL_SLIDE_DISTANCE}px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  @keyframes navMenuContentFromEnd {
    from {
      opacity: 0;
      transform: translateX(${HORIZONTAL_SLIDE_DISTANCE}px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  @keyframes navMenuContentToStart {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(-${HORIZONTAL_SLIDE_DISTANCE}px);
    }
  }
  @keyframes navMenuContentToEnd {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(${HORIZONTAL_SLIDE_DISTANCE}px);
    }
  }
`

const viewportPositionerCSS = css`
  position: absolute;
  top: 100%;
  left: 0;
  padding-top: 8px;
  z-index: 1000;
`

const VIEWPORT_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)'

const viewportOuterGlassCSS = css`
  display: inline-block;
  padding: 4px;
  background: color-mix(
    in srgb,
    color-mix(in srgb, var(--moon-dust), var(--moon-dust-lighter)) 80%,
    transparent
  );
  border: 1px solid rgba(0, 0, 0, 0.07);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  opacity: 0;
  transform: translateY(-8px);
  pointer-events: none;
  transition:
    opacity 0.18s ${VIEWPORT_EASE},
    transform 0.22s ${VIEWPORT_EASE};
  will-change: opacity, transform;
  &:has([data-state='open']) {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`

const viewportCSS = css`
  position: relative;
  box-sizing: content-box;
  width: var(--radix-navigation-menu-viewport-width, max-content);
  height: var(--radix-navigation-menu-viewport-height, auto);
  border-radius: 16px;
  overflow: hidden;
  transition:
    width 0.25s ${VIEWPORT_EASE},
    height 0.25s ${VIEWPORT_EASE};
  will-change: width, height, opacity, transform;
  &[data-state='open'] {
    animation: viewportEntry 0.22s ${VIEWPORT_EASE} both;
  }
  &[data-state='closed'] {
    animation: viewportExit 0.22s ${VIEWPORT_EASE} both;
  }
  @keyframes viewportEntry {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes viewportExit {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-8px);
    }
  }
`

const getItemId = (item: NavMenuItem, index: number) =>
  item.id ?? `nav-${index}`

const hasColumns = (item: NavMenuItem) =>
  !!item.columns && item.columns.length > 0

// First child item across all columns that carries a quote — used as the
// default the panel shows when a menu first opens, before the user hovers.
const firstQuoteItem = (item: NavMenuItem): NavMenuChildItem | undefined =>
  item.columns?.flatMap((c) => c.items).find((i) => !!i.quote)

export const NavMenu = ({ items }: Props) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  // URL of the child card currently under the cursor — drives which quote
  // the panel shows. Reset to null whenever the open menu changes; render
  // falls back to that menu's first quote item.
  const [hoveredChildUrl, setHoveredChildUrl] = useState<string | null>(null)
  // Controlled open value so we can force the dropdown shut when a link inside
  // it is clicked (otherwise Radix keeps it open on hover after navigation).
  const [openValue, setOpenValue] = useState('')
  const closeMenu = () => setOpenValue('')

  return (
    <NavigationMenu.Root
      css={rootCSS}
      delayDuration={50}
      skipDelayDuration={300}
      value={openValue}
      onValueChange={(value) => {
        setOpenValue(value)
        if (value) setHoveredId(value)
        setHoveredChildUrl(null)
      }}
      onPointerLeave={() => setHoveredId(null)}
    >
      <NavigationMenu.List css={listCSS}>
        {items.map((item, index) => {
          const id = getItemId(item, index)
          const itemHasSubmenu = hasColumns(item)
          const isActive = hoveredId === id

          if (itemHasSubmenu) {
            const defaultQuoteItem = firstQuoteItem(item)
            const activeQuoteItem =
              item.columns
                ?.flatMap((c) => c.items)
                .find((i) => !!i.quote && i.url === hoveredChildUrl) ??
              defaultQuoteItem
            const activeQuote = activeQuoteItem?.quote
            return (
              <NavigationMenu.Item key={id} value={id} css={itemCSS}>
                <NavigationMenu.Trigger
                  css={triggerCSS}
                  className="shade-05"
                  onPointerEnter={() => setHoveredId(id)}
                  onClick={(e) => e.preventDefault()}
                >
                  {isActive && <HoverPill />}
                  <span css={triggerLabelCSS}>{item.title}</span>
                  <span css={chevronCSS}>
                    <ChevronDownIcon size={10} />
                  </span>
                </NavigationMenu.Trigger>

                <NavigationMenu.Content css={contentCSS}>
                  <div css={primaryCardCSS}>
                    {item.columns?.map((column, columnIndex) => (
                      <NavMenuColumnView
                        key={column.title ?? `col-${columnIndex}`}
                        column={column}
                        onChildPointerEnter={setHoveredChildUrl}
                        onItemClick={closeMenu}
                      />
                    ))}
                  </div>
                  {/* Quote panel renders between the primary card and any
                      secondary section — the user's stated ordering. */}
                  {activeQuote && (
                    <NavMenuQuotePanel
                      quote={activeQuote}
                      quoteKey={activeQuoteItem?.url ?? 'default'}
                    />
                  )}
                  {!!item.secondaryColumns?.length && (
                    <div css={secondarySectionCSS}>
                      {item.secondaryColumns.map((column, columnIndex) => (
                        <NavMenuSecondaryColumn
                          key={column.title ?? `sec-${columnIndex}`}
                          column={column}
                          onItemClick={closeMenu}
                        />
                      ))}
                    </div>
                  )}
                </NavigationMenu.Content>
              </NavigationMenu.Item>
            )
          }

          return (
            <NavigationMenu.Item key={id} css={itemCSS}>
              <NavigationMenu.Link
                href={item.url ?? '#'}
                css={triggerCSS}
                className="shade-05"
                onPointerEnter={() => setHoveredId(id)}
              >
                {isActive && <HoverPill />}
                <span css={triggerLabelCSS}>{item.title}</span>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
          )
        })}
      </NavigationMenu.List>

      {/* force-light-mode keeps the dropdown panel readable even when the bar
          itself is inverted (dark-mode) over a hero. */}
      <div css={viewportPositionerCSS} className="force-light-mode">
        <div css={viewportOuterGlassCSS}>
          <NavigationMenu.Viewport css={viewportCSS} />
        </div>
      </div>
    </NavigationMenu.Root>
  )
}

const HoverPill = () => (
  <motion.span
    layoutId={HOVER_INDICATOR_LAYOUT_ID}
    css={hoverIndicatorCSS}
    transition={{ type: 'spring', stiffness: 1000, damping: 42, mass: 0.35 }}
  />
)

const NavMenuColumnView = ({
  column,
  onChildPointerEnter,
  onItemClick,
}: {
  column: NavMenuColumn
  // Called with the child's url when its card is hovered — only fires for
  // children that carry a quote, so non-quote menus see no events.
  onChildPointerEnter?: (url: string) => void
  // Closes the dropdown when a card is clicked.
  onItemClick?: () => void
}) => (
  <div className="flexbox flexbox-column" style={{ width: 260 }}>
    {column.title ? (
      <div css={columnTitleCSS}>{column.title}</div>
    ) : (
      column.continuesPreviousColumn && (
        <div css={columnTitleCSS} aria-hidden>
          &nbsp;
        </div>
      )
    )}
    {column.items.map((subItem, itemIndex) => (
      <NavMenuCard
        key={subItem.url}
        icon={subItem.icon}
        title={subItem.title}
        subtitle={subItem.subtitle}
        url={subItem.url}
        image={itemIndex === 0 ? column.feature : undefined}
        onClick={onItemClick}
        onPointerEnter={
          subItem.quote && onChildPointerEnter
            ? () => onChildPointerEnter(subItem.url)
            : undefined
        }
      />
    ))}
  </div>
)

// Quote panel rendered in the dropdown to the right of the primary card.
// Crossfades between quotes via AnimatePresence when the user hovers a
// different category card.
const NavMenuQuotePanel = ({
  quote,
  quoteKey,
}: {
  quote: NavMenuQuote
  // Unique identifier (we pass the source item's url) so AnimatePresence
  // remounts the inner content on swap and the crossfade plays.
  quoteKey: string
}) => (
  <div css={quotePanelCSS}>
    <AnimatePresence mode="wait">
      <motion.div
        key={quoteKey}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
        className="flexbox flexbox-column"
        style={{ gap: 14 }}
      >
        <div css={quoteOrbitWrapperCSS}>
          <OrbitWithImage
            pictureUrl={quote.pictureUrl}
            companyImageUrl={quote.companyImageUrl}
            name={quote.name}
            height="100px"
          />
        </div>
        <p css={quoteTestimonialCSS}>“{quote.testimonial}”</p>
        <div css={quoteAttributionWrapperCSS}>
          <span css={quoteNameCSS}>{quote.name}</span>
          <span css={quoteTitleCSS}>{quote.title}</span>
        </div>
      </motion.div>
    </AnimatePresence>
  </div>
)

const NavMenuSecondaryColumn = ({
  column,
  onItemClick,
}: {
  column: NavMenuColumn
  onItemClick?: () => void
}) => (
  <div className="flexbox flexbox-column" style={{ minWidth: 180 }}>
    {column.title && <div css={columnTitleCSS}>{column.title}</div>}
    {column.items.map((subItem) => (
      <a
        key={subItem.url}
        href={subItem.url}
        css={secondaryLinkCSS}
        onClick={onItemClick}
      >
        {subItem.title}
      </a>
    ))}
  </div>
)
